import { canonicalLineageSchema } from "@repo/domain";
import { z } from "zod";
import { newCanonicalSchema } from "./entity-id.schemas";

const memberIdSchema = z.string().regex(/^[a-z][a-z0-9]*-[1-9]\d*$/);
const clientSectionIdSchema = z.string().regex(/^section-[0-9a-f-]{36}$/);

/** A bare canonical url. `|version` is refused because a catalog reference names a lineage. */
const unpinnedCanonicalSchema = z
  .string()
  .url()
  .refine((value) => !value.includes("|"), {
    message: "A catalog reference identifies a lineage, not a revision",
  });

/**
 * A placeholder: the reference and the two facts that are the
 * exam's rather than the registry's. Nothing the registry owns appears here —
 * no label, no cardinality, no options — because an exam author cannot edit
 * registry content, not even as a delta.
 */
const findingSchema = z.object({
  finding: unpinnedCanonicalSchema,
  prerequisite: canonicalLineageSchema.optional(),
});

const sectionSchema = z.object({
  id: z.union([memberIdSchema, clientSectionIdSchema]).optional(),
  label: z.string().trim().min(1),
  findings: z.array(findingSchema).min(1),
});

const examDefinitionContentSchema = z.object({
  examNoteLabel: z.string().trim().min(1).optional(),
  sections: z.array(sectionSchema).min(1),
});

export const defineExamDefinitionInputSchema = examDefinitionContentSchema.extend({
  definitionUrl: newCanonicalSchema,
  name: z.string().trim().min(1),
  description: z.string().trim().min(1).optional(),
});
export type DefineExamDefinitionInput = z.infer<typeof defineExamDefinitionInputSchema>;

export const reviseExamDefinitionInputSchema = examDefinitionContentSchema.extend({
  definitionUrl: z.string().url(),
  expectedVersion: z.number().int().positive(),
});
export type ReviseExamDefinitionInput = z.infer<typeof reviseExamDefinitionInputSchema>;

export const correctExamDefinitionInputSchema = z.object({
  definitionUrl: z.string().url(),
  name: z.string().trim().min(1).optional(),
  description: z.string().trim().min(1).nullable().optional(),
});
export type CorrectExamDefinitionInput = z.infer<typeof correctExamDefinitionInputSchema>;

export const changeExamDefinitionAvailabilityInputSchema = z.object({
  definitionUrl: z.string().url(),
});
export type ChangeExamDefinitionAvailabilityInput = z.infer<typeof changeExamDefinitionAvailabilityInputSchema>;
